import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Search, Filter, Download, Check, X, Flag, Mail, Phone,
  Save, Users, Send, RefreshCw, Plus, Eye, Clock, CheckCircle,
  AlertCircle, ChevronDown, MapPin, Calendar, Inbox,
  FileText, Star, BarChart2, ArrowRight, ChevronRight,Globe, GraduationCap, ClipboardList, Target
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");
const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB"); } catch { return d; }
};

const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;

const getDownloadUrl = (file) => {
  if (!file) return "";
  if (/^https?:\/\//i.test(file)) return file;
  const fileName = String(file).split("/").pop();
  return `${DOWNLOAD_BASE}${fileName}`;
};

const getFileName = (file) => String(file || "").split("/").pop();

const csvCell = (value) =>
  `"${String(value ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;

const downloadCSV = (filename, headers, rows) => {
  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\n");

  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#0a2352",
  danger:      "#374151",
  warn:        "#d97706",
  purple:      "#7c3aed",
  accent:      "#2563eb",
};
const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_META = {
  applied:          { label: "Applied",          bg: "#dbeafe", color: "#1d4ed8" },
  under_review:     { label: "Under Review",     bg: "#fef3c7", color: "#92400e" },
  shortlisted:      { label: "Shortlisted",      bg: "#e0e7ff", color: "#3730a3" },
  rejected:         { label: "Rejected",         bg: "#e5e7eb", color: "#374151" },
  pending:          { label: "Pending",          bg: "#fef3c7", color: "#92400e" },
  accepted:         { label: "Accepted",         bg: "#dbeafe", color: "#0a2352" },
  declined:         { label: "Declined",         bg: "#e5e7eb", color: "#374151" },
  withdrawn:        { label: "Withdrawn",        bg: "#f3f4f6", color: "#6b7280" },
  expired:          { label: "Expired",          bg: "#f3f4f6", color: "#6b7280" },
  invited:          { label: "Invited",          bg: "#ede9fe", color: "#5b21b6" },
  draft:            { label: "Draft",            bg: "#f3f4f6", color: "#6b7280" },
  published:        { label: "Published",        bg: "#dbeafe", color: "#1d4ed8" },
  registration_open:{ label: "Reg. Open",        bg: "#e0e7ff", color: "#3730a3" },
  invitations_released:{ label: "Inv. Released", bg: "#ede9fe", color: "#5b21b6" },
  completed:        { label: "Completed",        bg: "#dbeafe", color: "#0a2352" },
};
const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: (status || "").replace(/_/g, " "), bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
};


const inputBase = {
  width: "100%", padding: "8px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 13, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const Input = ({ style = {}, ...props }) => (
  <input {...props} style={{ ...inputBase, ...style }} />
);
const Select = ({ style = {}, ...props }) => (
  <select {...props} style={{ ...inputBase, ...style }} />
);

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const pad = size === "sm" ? "6px 13px" : "9px 20px";
  const fs  = size === "sm" ? 12 : 13;
  const variants = {
    primary:   { background: T.navy,         color: "#fff",   border: "none" },
    outline:   { background: "transparent",  color: T.navy,   border: `1.5px solid ${T.border}` },
    success:   { background: T.navy,         color: "#fff",   border: "none" },
    danger:    { background: "#374151",      color: "#fff",   border: "none" },
    warn:      { background: T.warn,         color: "#fff",   border: "none" },
    ghost:     { background: "transparent",  color: T.navy,   border: "none" },
    purple:    { background: T.purple,       color: "#fff",   border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", padding: pad, fontSize: fs, opacity: disabled ? 0.6 : 1, fontFamily: "inherit", ...variants[variant], ...style }}>
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

const EmptyState = ({ icon: Icon = Inbox, title, sub }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, color: T.muted, gap: 10 }}>
    {Icon && <Icon size={36} color={T.border} />}
    <div style={{ fontWeight: 700, fontSize: 14, color: T.muted }}>{title}</div>
    {sub && <div style={{ fontSize: 12, color: T.border }}>{sub}</div>}
  </div>
);

const ProgressBar = ({ value, color }) => (
  <div style={{ height: 5, background: T.light, borderRadius: 99, overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 99, transition: "width .3s" }} />
  </div>
);

const StatChip = ({ label, value, color }) => (
  <div style={{ background: color + "15", borderRadius: 8, padding: "8px 14px", display: "flex", flexDirection: "column", gap: 2, minWidth: 90 }}>
    <div style={{ fontSize: 20, fontWeight: 900, color }}>{value}</div>
    <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
  </div>
);

// ── Event Selector Dropdown ───────────────────────────────────────────────────
function EventSelector({ events, selectedEvent, onSelect, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${T.border}`,
          background: "#fff", cursor: "pointer", fontFamily: "inherit",
          minHeight: 44,
        }}>
        <Calendar size={15} color={T.orange} />
        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          {loading ? (
            <span style={{ fontSize: 13, color: T.muted }}>Loading events…</span>
          ) : selectedEvent ? (
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: T.navy,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={selectedEvent.event_name}
            >
              {selectedEvent.event_name}
            </div>
          ) : (
            <span style={{ fontSize: 13, color: T.muted }}>Select an event…</span>
          )}
        </div>
        <ChevronDown size={14} color={T.muted} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 9999,
          background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 10,
          boxShadow: "0 8px 30px rgba(10,35,82,.14)", maxHeight: 340, overflowY: "auto",
        }}>
          {events.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.muted }}>No events found</div>
          ) : events.map(ev => (
            <button key={ev.primary_id} onClick={() => { onSelect(ev); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 14px",
                border: "none", borderBottom: `1px solid ${T.light}`, cursor: "pointer", fontFamily: "inherit",
                background: selectedEvent?.primary_id === ev.primary_id ? T.light : "transparent", textAlign: "left",
              }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{ev.event_name}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                  {ev.city}, {ev.country} · {fmtDate(ev.event_start_date)} → {fmtDate(ev.event_end_date)}
                </div>
              </div>
              <Badge status={ev.event_status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — APPLICANT REVIEW
// ══════════════════════════════════════════════════════════════════════════════

// JSON columns (participation_interest, recruitment_levels, areas_of_interest)
// may arrive as arrays, JSON strings, or plain strings depending on the driver.
const parseList = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    if (Array.isArray(parsed)) return parsed;
    if (parsed) return [String(parsed)];
    return [];
  } catch {
    return String(val).split(",").map(s => s.trim()).filter(Boolean);
  }
};

const getInstitutionType = (ap) => {
  if (!ap) return "—";
  if ((ap.institution_type || "").toLowerCase() === "other" && ap.other_institution_type) {
    return ap.other_institution_type;
  }
  return ap.institution_type || "—";
};


// ── Shared primitives ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(10,35,82,.06)", overflow: "hidden", fontFamily: FONT, ...style }}>
    {children}
  </div>
);



// Tag pill list — used for Participation Interest / Recruitment Levels
const TagList = ({ items, color = T.accent, emptyLabel = "Not specified" }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
    {items.length === 0 ? (
      <span style={{ fontSize: 13, color: T.muted, fontStyle: "italic" }}>{emptyLabel}</span>
    ) : (
      items.map((it, i) => (
        <span key={i} style={{ background: color + "15", color, padding: "5px 12px", borderRadius: 7, fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>
          {it}
        </span>
      ))
    )}
  </div>
);

// Detail row — label / value pair
const DetailRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${T.light}`, fontSize: 13.5, gap: 12 }}>
    <span style={{ color: T.muted, fontWeight: 600, flexShrink: 0 }}>{label}</span>
    <span style={{ color: T.text, fontWeight: 700, textAlign: "right" }}>{value || "—"}</span>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// UNIVERSITY APPLICANT REVIEW — Foreign University Applicants
// ══════════════════════════════════════════════════════════════════════════════
function ApplicantReview({ selectedEvent, uc }) {
  const [applicants, setApplicants]       = useState([]);
  const [loading, setLoading]             = useState(false);
  const [selected, setSelected]           = useState(null);
  const [search, setSearch]               = useState("");
  const [statusFilter, setStatusFilter]   = useState("");
  const [note, setNote]                   = useState("");
  const [notes, setNotes]                 = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  // SELECT a.*, up.* FROM edu_platform.event_applications a
  // JOIN edu_platform.foreign_university_profile up ON a.applicant_entity_id = up.entity_id
  // WHERE a.event_id = ${selectedEvent.primary_id}
  // AND a.tenant_id = ${uc.tenants_id} AND a.is_active = 1
  const fetchApplicants = async () => {
    if (!selectedEvent) return;
    setLoading(true);
    setSelected(null);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT a.primary_id AS id, a.additional_notes,
                       up.university_name, up.country, up.institution_type, up.other_institution_type,
                       up.participation_interest, up.participation_other,
                       up.recruitment_levels, up.university_logo,
                       up.rep_full_name, up.rep_designation, up.rep_email, up.rep_mobile, up.rep_whatsapp,
                       a.application_status, a.review_status,
                       a.applied_on, a.reviewed_on, a.num_representatives
                FROM edu_platform.event_applications a
                JOIN edu_platform.foreign_university_profile up ON a.applicant_user_id = up.primary_id
                WHERE a.event_id = ${selectedEvent.primary_id}
                  AND a.tenant_id = ${uc.tenants_id}
                  AND a.is_active = 1
                ORDER BY a.applied_on DESC;`,
      }, creds[0]);
      setApplicants(res.data || []);
    } catch (err) { console.error("Fetch university applicants error:", err); }
    finally { setLoading(false); }
  };

  const fetchNotes = async (appId) => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT rn.note_text, rn.created_on, rn.reviewer_user_id
                FROM edu_platform.reviewer_notes rn
                WHERE rn.application_id = ${appId}
                  AND rn.event_id = ${selectedEvent.primary_id}
                ORDER BY rn.created_on DESC LIMIT 10;`,
      }, creds[0]);
      setNotes(res.data || []);
    } catch (err) { console.error("Fetch notes error:", err); }
  };

  useEffect(() => { fetchApplicants(); }, [selectedEvent]);

  const selectApplicant = (ap) => {
    setSelected(ap);
    fetchNotes(ap.id);
    setNote("");
  };

  // UPDATE edu_platform.event_applications
  // SET application_status='${action}', modified_date=NOW(), modified_by=${uc.user_id}
  // WHERE primary_id=${id} AND event_id=${selectedEvent.primary_id}
  const handleAction = async (id, action) => {
    setActionLoading(action);
    try {
      const creds = Fetchupdatequery();
      const now = getFormattedDate();
      await axios.post(creds[1], {
        schema_name: "edu_platform",
        table: "event_applications",
        set_column: [{
          application_status: action,
          modified_date: now,
          modified_by: uc.user_id,
        }],
        where: [{
          primary_id: id,
          event_id: selectedEvent.primary_id,
          tenant_id: uc.tenants_id,
        }],
      }, creds[0]);
      setApplicants(p => p.map(a => a.id === id ? { ...a, application_status: action } : a));
      setSelected(p => ({ ...p, application_status: action }));
    } catch (err) { console.error("Action error:", err); }
    finally { setActionLoading(null); }
  };

  // INSERT INTO edu_platform.reviewer_notes (application_id, event_id, reviewer_user_id, note_text, created_on)
  const handleSaveNote = async (appId) => {
    if (!note.trim()) return;
    try {
      const creds = CreateData();
      const now = getFormattedDate();
      await axios.post(creds[1], {
        database: "edu_platform",
        table: "reviewer_notes",
        formData: {
          application_id: appId,
          event_id: selectedEvent.primary_id,
          reviewer_user_id: uc.user_id,
          applicant_entity_id: uc.selectedentity,
          note_text: note,
          created_on: now,
          is_active: 1,
        },
      }, creds[0]);
      setNotes(p => [{ note_text: note, created_on: now, reviewer_user_id: uc.user_id }, ...p]);
      setNote("");
    } catch (err) { console.error("Save note error:", err); }
  };

  const filtered = applicants.filter(a =>
    (!search || (a.university_name || "").toLowerCase().includes(search.toLowerCase()) ||
                (a.country || "").toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || a.application_status === statusFilter)
  );

  const counts = applicants.reduce((acc, a) => { acc[a.application_status] = (acc[a.application_status] || 0) + 1; return acc; }, {});

  if (!selectedEvent) {
    return <EmptyState icon={Calendar} title="Select an event" sub="Choose an event above to review its university applicants" />;
  }

  return (
    <div style={{ display: "flex",minHeight: 500 }}>
      {/* LEFT PANEL — University list */}
      <div style={{ width: 340, flexShrink: 0, borderRight: `1px solid ${T.border}`, background: "#fff", display: "flex", flexDirection: "column" }}>
        {/* Stats row */}
        <div style={{ padding: "13px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <StatChip label="Total"       value={applicants.length}          color={T.accent} />
          <StatChip label="Shortlisted" value={counts.shortlisted || 0}    color={T.navy} />
          <StatChip label="Rejected"    value={counts.rejected || 0}       color="#374151" />
          <StatChip label="Review"      value={counts.under_review || 0}   color={T.warn} />
        </div>

        {/* Search + Filter */}
        <div style={{ padding: "11px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search universities, country…" style={{ paddingLeft: 32, fontSize: 13.5 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 13.5 }}>
            <option value="">All Statuses</option>
            {["registered", "under_review", "shortlisted", "rejected"].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </Select>
        </div>

        {/* Count row */}
        <div style={{ padding: "7px 12px", background: T.light, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, fontWeight: 700, color: T.muted }}>
          <span>{filtered.length} results</span>
          <Btn variant="ghost" size="sm" icon={RefreshCw} onClick={fetchApplicants} style={{ padding: "3px 8px", fontSize: 12 }}>Refresh</Btn>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <EmptyState icon={RefreshCw} title="Loading applicants…" />
          ) : filtered.length === 0 ? (
            <EmptyState title="No applicants" sub="Adjust filters to see results" />
          ) : filtered.map(ap => (
            <div key={ap.id} onClick={() => selectApplicant(ap)}
              style={{ padding: "14px 16px", borderBottom: `1px solid ${T.light}`, cursor: "pointer", background: selected?.id === ap.id ? T.light : "transparent", transition: "background .15s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: T.text, flex: 1, lineHeight: 1.3 }}>{ap.university_name}</div>
                <Badge status={ap.application_status} />
              </div>
              <div style={{ fontSize: 12.5, color: T.muted, display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                <Globe size={12} /> {ap.country || "—"}
              </div>
              <div style={{ fontSize: 12.5, color: T.muted, display: "flex", alignItems: "center", gap: 5 }}>
                <GraduationCap size={12} /> {getInstitutionType(ap)}
                <span>·</span>
                <span>{fmtDate(ap.applied_on)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL — Detail */}
      <div style={{ flex: 1, overflowY: "auto", background: T.bg }}>
        {selected ? (
          <div style={{ padding: "15px 15px" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10, gap: 14, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                {selected.university_logo ? (
                  <div style={{ width: 200, height: 80, borderRadius: 12, border: `1.5px solid ${T.border}`, background: "#fff", overflow: "hidden", flexShrink: 0 }}>
                    <img
                      src={getDownloadUrl(selected.university_logo)}
                      alt={selected.university_name}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      onError={e => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement.style.background = T.navy;
                        e.currentTarget.parentElement.style.display = "flex";
                        e.currentTarget.parentElement.style.alignItems = "center";
                        e.currentTarget.parentElement.style.justifyContent = "center";
                        e.currentTarget.parentElement.innerHTML = `<span style="color:#fff;font-weight:900;font-size:22px">${(selected.university_name || "?")[0]}</span>`;
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ width: 52, height: 52, background: T.navy, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 22, flexShrink: 0 }}>
                    {(selected.university_name || "?")[0]}
                  </div>
                )}
                <div>
                  <h2 style={{ margin: 0, fontSize: 19, fontWeight: 900, color: T.text }}>{selected.university_name}</h2>
                  <div style={{ fontSize: 13.5, color: T.muted, marginTop: 2 }}>{selected.country} · {getInstitutionType(selected)}</div>
                  <div style={{ marginTop: 6 }}><Badge status={selected.application_status} /></div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn variant="success" size="sm" icon={Check}
                  onClick={() => handleAction(selected.id, "shortlisted")}
                  disabled={!!actionLoading || selected.application_status === "shortlisted"}>
                  {actionLoading === "shortlisted" ? "…" : "Shortlist"}
                </Btn>
                <Btn variant="danger" size="sm" icon={X}
                  onClick={() => handleAction(selected.id, "rejected")}
                  disabled={!!actionLoading || selected.application_status === "rejected"}>
                  {actionLoading === "rejected" ? "…" : "Reject"}
                </Btn>
                <Btn variant="warn" size="sm" icon={Flag}
                  onClick={() => handleAction(selected.id, "under_review")}
                  disabled={!!actionLoading}>
                  Mark Review
                </Btn>
              </div>
            </div>

            {/* Detail grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {/* University Information */}
              <Card style={{ padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.light}` }}>
                  University Information
                </div>
                <DetailRow label="Name of University / Institution" value={selected.university_name} />
                <DetailRow label="Country" value={selected.country} />
                <DetailRow label="Type of Institution" value={getInstitutionType(selected)} />
                <DetailRow label="Review Status" value={selected.review_status} />
                <DetailRow label="Registered On" value={fmtDate(selected.applied_on)} />
              </Card>

              {/* Participation & Recruitment */}
              <Card style={{ padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.light}` }}>
                  Participation &amp; Recruitment
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                    <ClipboardList size={14} color={T.accent} /> Participation Interest
                  </div>
                  <TagList
                    items={[
                      ...parseList(selected.participation_interest),
                      ...(selected.participation_other ? [selected.participation_other] : []),
                    ]}
                    color={T.accent}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                    <Target size={14} color={T.purple} /> Preferred Levels of Recruitment
                  </div>
                  <TagList items={parseList(selected.recruitment_levels)} color={T.purple} />
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                    <Calendar size={14} color={T.warn} /> Event Participation Requirements
                  </div>
                  {selected.additional_notes ? (
                    <div style={{ fontSize: 13.5, color: T.text, lineHeight: 1.6, background: T.light, borderRadius: 8, padding: "10px 12px" }}>
                      {selected.additional_notes}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: T.muted, fontStyle: "italic" }}>None specified</div>
                  )}
                </div>
              </Card>

              {/* Representative */}
              <Card style={{ padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.light}` }}>
                  Primary Representative
                </div>
                <DetailRow label="Name" value={selected.rep_full_name} />
                <DetailRow label="Designation" value={selected.rep_designation} />
                <DetailRow label="Email" value={selected.rep_email} />
                <DetailRow label="Mobile" value={selected.rep_mobile} />
                <DetailRow label="Representatives" value={selected.num_representatives} />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <Btn variant="outline" size="sm" icon={Mail}>Email</Btn>
                  <Btn variant="outline" size="sm" icon={Phone}>Call</Btn>
                </div>
              </Card>

              {/* Reviewer Notes */}
              <Card style={{ padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.light}` }}>
                  Reviewer Notes
                </div>
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Add a private note…"
                  style={{ ...inputBase, minHeight: 76, resize: "vertical", marginBottom: 10, fontSize: 13.5 }} />
                <Btn variant="outline" size="sm" icon={Save} onClick={() => handleSaveNote(selected.id)}>Save Note</Btn>
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {notes.map((n, i) => (
                    <div key={i} style={{ padding: "10px 12px", background: T.light, borderRadius: 8 }}>
                      <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{n.note_text}</div>
                      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>Reviewer #{n.reviewer_user_id} · {fmtDate(n.created_on)}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        ) : (
          <EmptyState icon={Users} title="Select a university" sub="Click on a university to view its full application" />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — INVITATION MANAGEMENT
// ══════════════════════════════════════════════════════════════════════════════
function InvitationManagement({ selectedEvent, uc }) {
  const [invitations, setInvitations]     = useState([]);
  const [loading, setLoading]             = useState(false);
  const [search, setSearch]               = useState("");
  const [statusFilter, setStatusFilter]   = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [shortlisted, setShortlisted]     = useState([]);
  const [modalData, setModalData]         = useState({ applicantId: "", deadline: "", message: "" });
  const [editInvitationId, setEditInvitationId] = useState(null);

  // SELECT i.*, sp.school_name, sp.country, sp.rep1_name
  // FROM edu_platform.event_invitations i
  // JOIN edu_platform.event_applications a ON i.application_id = a.primary_id
  // JOIN edu_platform.school_profiles sp ON a.applicant_entity_id = sp.entity_id
  // WHERE i.event_id = ${selectedEvent.primary_id}
  // AND i.creator_user_id = ${uc.user_id} AND i.tenant_id = ${uc.tenants_id}
  const fetchInvitations = async () => {
    if (!selectedEvent) return;
    setLoading(true);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT i.primary_id AS id, i.application_id AS applicantId, i.invitation_status, i.invited_on,
                       i.acceptance_deadline, i.responded_on, i.custom_message,
                       sp.school_name, sp.country, sp.rep1_name AS rep
                FROM edu_platform.event_invitations i
                JOIN edu_platform.event_applications a ON i.application_id = a.primary_id
                JOIN edu_platform.school_profile sp ON a.applicant_user_id = sp.primary_id
                WHERE i.event_id = ${selectedEvent.primary_id}
                  AND i.tenant_id = ${uc.tenants_id}
                  AND i.is_active = 1
                ORDER BY i.invited_on DESC;`,
      }, creds[0]);
      setInvitations(res.data || []);
    } catch (err) { console.error("Fetch invitations error:", err); }
    finally { setLoading(false); }
  };

  const fetchShortlisted = async () => {
    if (!selectedEvent) return;
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT a.primary_id, sp.school_name, sp.country
                FROM edu_platform.event_applications a
                JOIN edu_platform.school_profile sp ON a.applicant_user_id = sp.primary_id
                WHERE a.event_id = ${selectedEvent.primary_id}
                  AND a.application_status = 'shortlisted'
                  AND a.tenant_id = ${uc.tenants_id}
                  AND a.is_active = 1;`,
      }, creds[0]);
      setShortlisted(res.data || []);
    } catch (err) { console.error("Fetch shortlisted error:", err); }
  };

  const handleExport = () => {
    const headers = [
      "School / Applicant",
      "Country",
      "Representative",
      "Invited On",
      "Acceptance Deadline",
      "Status",
      "Responded On",
      "Custom Message",
    ];

    const rows = filtered.map((inv) => ([
      inv.school_name || "",
      inv.country || "",
      inv.rep || "",
      fmtDate(inv.invited_on),
      fmtDate(inv.acceptance_deadline),
      inv.invitation_status || "",
      fmtDate(inv.responded_on),
      inv.custom_message || "",
    ]));

    downloadCSV(
      `invitations-${selectedEvent?.primary_id || "event"}-${new Date().toISOString().slice(0, 10)}.csv`,
      headers,
      rows
    );
  };

  useEffect(() => { fetchInvitations(); }, [selectedEvent]);

  // UPDATE edu_platform.event_invitations SET invitation_status='withdrawn' WHERE primary_id=${id}
  const handleAction = async (id, action) => {
    setActionLoading({ id, action });
    try {
      const creds = Fetchupdatequery();
      const now = getFormattedDate();
      const newStatus = action === "withdraw" ? "withdrawn" : "pending";
      await axios.post(creds[1], {
        schema_name: "edu_platform",
        table: "event_invitations",
        set_column: [{
          invitation_status: newStatus,
          modified_date: now,
        }],
        where: [{
          primary_id: id,
          event_id: selectedEvent.primary_id,
          tenant_id: uc.tenants_id,
        }],
      }, creds[0]);
      setInvitations(p => p.map(inv => inv.id === id ? { ...inv, invitation_status: newStatus } : inv));
    } catch (err) { console.error("Invitation action error:", err); }
    finally { setActionLoading(null); }
  };

  const openNewInvitation = async () => {
    await fetchShortlisted();
    setEditInvitationId(null);
    setModalData({ applicantId: "", deadline: "", message: "" });
    setShowModal(true);
  };

  const openReinvite = async (inv) => {
    await fetchShortlisted();
    setEditInvitationId(inv.id);
    setModalData({
      applicantId: inv.applicantId || "",
      deadline: inv.acceptance_deadline ? String(inv.acceptance_deadline).slice(0, 10) : "",
      message: inv.custom_message || "",
    });
    setShowModal(true);
  };

  const handleSendInvitation = async () => {
    if (!modalData.applicantId || !modalData.deadline) return;
    try {
      const now = getFormattedDate();
      if (editInvitationId) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "event_invitations",
          set_column: [{
            application_id: modalData.applicantId,
            applicant_entity_id: uc.selectedentity,
            invitation_status: "pending",
            invited_on: now,
            acceptance_deadline: modalData.deadline,
            custom_message: modalData.message || null,
            modified_date: now,
            modified_by: uc.user_id,
          }],
          where: [{
            primary_id: editInvitationId,
            event_id: selectedEvent.primary_id,
            tenant_id: uc.tenants_id,
          }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        await axios.post(creds[1], {
          database: "edu_platform",
          table: "event_invitations",
          formData: {
            event_id: selectedEvent.primary_id,
            application_id: modalData.applicantId,
            applicant_entity_id : uc.selectedentity,
            creator_user_id: uc.user_id,
            invitation_status: "pending",
            invited_on: now,
            acceptance_deadline: modalData.deadline,
            custom_message: modalData.message || null,
            tenant_id: uc.tenants_id,
            is_active: 1,
            created_date: now,
          },
        }, creds[0]);
      }
      fetchInvitations();
      setShowModal(false);
      setModalData({ applicantId: "", deadline: "", message: "" });
      setEditInvitationId(null);
    } catch (err) { console.error("Send invitation error:", err); }
  };

  const filtered = invitations.filter(inv =>
    (!search || (inv.school_name || "").toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || inv.invitation_status === statusFilter)
  );

  const counts = invitations.reduce((acc, i) => { acc[i.invitation_status] = (acc[i.invitation_status] || 0) + 1; return acc; }, {});

  if (!selectedEvent) {
    return <EmptyState icon={Calendar} title="Select an event" sub="Choose an event above to manage its invitations" />;
  }

  return (
    <div style={{ padding: "0 0 32px" }}>
      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <StatChip label="Total Sent"  value={invitations.length}       color={T.accent} />
        <StatChip label="Pending"     value={counts.pending || 0}      color={T.warn} />
        <StatChip label="Accepted"    value={counts.accepted || 0}     color={T.navy} />
        <StatChip label="Declined"    value={counts.declined || 0}     color="#374151" />
        <StatChip label="Expired"     value={counts.expired || 0}      color={T.muted} />
        <div style={{ marginLeft: "auto" }}>
          <Btn variant="primary" icon={Plus} onClick={openNewInvitation}>
            Send New Invitation
          </Btn>
        </div>
      </div>

      {/* Filters */}
      <Card style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 10 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, position: "relative", minWidth: 200 }}>
            <Search size={12} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by school…" style={{ paddingLeft: 28, fontSize: 12 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 170, fontSize: 12 }}>
            <option value="">All Statuses</option>
            {["pending", "accepted", "declined", "expired", "withdrawn"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
          <Btn variant="outline" size="sm" icon={RefreshCw} onClick={fetchInvitations}>Refresh</Btn>
          <Btn variant="outline" size="sm" icon={Download} onClick={handleExport}>Export</Btn>
        </div>
      </Card>

      {/* Table */}
      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <EmptyState icon={RefreshCw} title="Loading invitations…" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: `linear-gradient(90deg, ${T.navy}, ${T.navyMid})` }}>
                  {["School / Applicant", "Country", "Representative", "Invited On", "Acceptance Deadline", "Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,.8)", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7}>
                    <EmptyState icon={Send} title="No invitations found" sub="Send a new invitation to get started" />
                  </td></tr>
                ) : filtered.map((inv, i) => {
                  const isLoading = actionLoading?.id === inv.id;
                  const isPastDeadline = inv.acceptance_deadline && new Date(inv.acceptance_deadline) < new Date();
                  return (
                    <tr key={inv.id} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? "#fff" : T.bg + "88" }}>
                      <td style={{ padding: "12px 14px", fontWeight: 700, fontSize: 13, color: T.navy }}>{inv.school_name}</td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: T.muted }}>{inv.country}</td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: T.muted }}>{inv.rep}</td>
                      <td style={{ padding: "12px 14px", fontSize: 11, color: T.muted, whiteSpace: "nowrap" }}>{fmtDate(inv.invited_on)}</td>
                      <td style={{ padding: "12px 14px", fontSize: 11, fontWeight: inv.invitation_status === "pending" ? 700 : 400, color: isPastDeadline ? "#374151" : inv.invitation_status === "pending" ? T.warn : T.muted, whiteSpace: "nowrap" }}>
                        {fmtDate(inv.acceptance_deadline)}
                        {isPastDeadline && inv.invitation_status === "pending" && <span style={{ marginLeft: 4, fontSize: 9, background: "#37415120", color: "#374151", borderRadius: 4, padding: "1px 5px" }}>EXPIRED</span>}
                      </td>
                      <td style={{ padding: "12px 14px" }}><Badge status={inv.invitation_status} /></td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {inv.invitation_status === "pending" && (
                            <Btn variant="outline" size="sm" icon={RefreshCw} onClick={() => handleAction(inv.id, "resend")} disabled={isLoading}>
                              {isLoading && actionLoading.action === "resend" ? "…" : "Resend"}
                            </Btn>
                          )}
                          {["pending", "accepted"].includes(inv.invitation_status) && (
                            <Btn variant="danger" size="sm" icon={X} onClick={() => handleAction(inv.id, "withdraw")} disabled={isLoading}>
                              {isLoading && actionLoading.action === "withdraw" ? "…" : "Withdraw"}
                            </Btn>
                          )}
                          {!["pending", "accepted"].includes(inv.invitation_status) && (
                            <Btn variant="purple" size="sm" icon={Send} onClick={() => openReinvite(inv)}>Re-invite</Btn>
                          )}
                          <Btn variant="ghost" size="sm" icon={Eye} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Send Invitation Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
          <Card style={{ width: 460, padding: 26, borderRadius: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.navy }}>Send Invitation</h3>
              <button onClick={() => { setShowModal(false); setEditInvitationId(null); setModalData({ applicantId: "", deadline: "", message: "" }); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} color={T.muted} /></button>
            </div>
            {selectedEvent && (
              <div style={{ padding: "9px 12px", background: T.light, borderRadius: 8, marginBottom: 14, fontSize: 12, color: T.navy, fontWeight: 600 }}>
                <Calendar size={11} style={{ marginRight: 5, verticalAlign: -1 }} />{selectedEvent.event_name}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.navy, display: "block", marginBottom: 5 }}>Select Shortlisted Applicant *</label>
              <Select value={modalData.applicantId} onChange={e => setModalData(p => ({ ...p, applicantId: e.target.value }))}>
                <option value="">— Choose applicant —</option>
                {shortlisted.map(a => (
                  <option key={a.primary_id} value={a.primary_id}>{a.school_name} — {a.country}</option>
                ))}
              </Select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.navy, display: "block", marginBottom: 5 }}>Acceptance Deadline *</label>
              <Input type="date" value={modalData.deadline} onChange={e => setModalData(p => ({ ...p, deadline: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.navy, display: "block", marginBottom: 5 }}>Custom Message (optional)</label>
              <textarea value={modalData.message} onChange={e => setModalData(p => ({ ...p, message: e.target.value }))}
                placeholder="Add a personal note to this invitation…"
                style={{ ...inputBase, minHeight: 70, resize: "vertical", fontSize: 12 }} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="outline" onClick={() => { setShowModal(false); setEditInvitationId(null); setModalData({ applicantId: "", deadline: "", message: "" }); }}>Cancel</Btn>
              <Btn variant="primary" icon={Send} onClick={handleSendInvitation}
                disabled={!modalData.applicantId || !modalData.deadline}>
                {editInvitationId ? "Update Invitation" : "Send Invitation"}
              </Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT: CreatorReviewHub
// ══════════════════════════════════════════════════════════════════════════════
export function CreatorReviewHub() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const [activeTab, setActiveTab]         = useState("applicants");
  const [events, setEvents]               = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // SELECT primary_id, event_name, city, country, event_start_date, event_end_date, event_status
  // FROM edu_platform.events
  // WHERE creator_user_id = ${uc.user_id} AND creator_entity_id = ${uc.selectedentity}
  // AND tenant_id = ${uc.tenants_id} AND is_active = 1
  // ORDER BY primary_id DESC
  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT primary_id, event_name, city, country, event_category,
                       event_start_date, event_end_date, event_status
                FROM edu_platform.events
                WHERE creator_entity_id = ${uc.selectedentity || uc.entities_id}
                  AND tenant_id = ${uc.tenants_id} AND division_id = ${uc.division[0]}
                  AND is_active = 1 AND event_status = "published"
                ORDER BY primary_id DESC;`,
      }, creds[0]);
      const data = res.data || [];
      setEvents(data);
      if (data.length > 0) setSelectedEvent(data[0]);
    } catch (err) { console.error("Fetch events error:", err); }
    finally { setEventsLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleExportEvent = () => {
    if (!selectedEvent) return;
    downloadCSV(
      `event-summary-${selectedEvent.primary_id || "selected"}-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Event ID", "Event Name", "Category", "City", "Country", "From Date", "To Date", "Status"],
      [[
        selectedEvent.primary_id || "",
        selectedEvent.event_name || "",
        selectedEvent.event_category || "",
        selectedEvent.city || "",
        selectedEvent.country || "",
        fmtDate(selectedEvent.event_start_date),
        fmtDate(selectedEvent.event_end_date),
        selectedEvent.event_status || "",
      ]]
    );
  };

  const TABS = [
    { key: "applicants",  label: "Applicant Review",      icon: Users     },
    { key: "invitations", label: "Invitation Management", icon: Send      },
  ];

  return (
    <div style={{ background: T.bg, fontFamily: FONT }}>

      {/* Tab bar + content */}
      <div style={{ padding: "10px" }}>
        {/* Tabs */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 10, marginTop: 7, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 4, background: T.card, borderRadius: 12, padding: 5, border: `1px solid ${T.border}`, width: "fit-content" }}>
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 7, padding: "9px 18px",
                    borderRadius: 9, border: "none", cursor: "pointer", fontFamily: "inherit",
                    fontWeight: 700, fontSize: 13, transition: "all .18s",
                    background: isActive ? `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` : "transparent",
                    color: isActive ? "#fff" : T.muted,
                  }}>
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto", flexWrap: "nowrap", justifyContent: "flex-end", minWidth: 0 }}>
            <div style={{ width: 280, flexShrink: 1, position: "relative" }}>
              <EventSelector
                events={events}
                selectedEvent={selectedEvent}
                onSelect={setSelectedEvent}
                loading={eventsLoading}
              />
            </div>
            <Btn
              variant="primary"
              size="sm"
              icon={Download}
              onClick={handleExportEvent}
              style={{
                background: T.navy,
                border: `1px solid ${T.navy}`,
                color: "#fff",
                boxShadow: "0 8px 18px rgba(10,35,82,.18)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Export
            </Btn>
          </div>
        </div>

        {/* Event context pill */}
        {/* {selectedEvent && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "7px 14px", background: T.navy + "10", borderRadius: 8, border: `1px solid ${T.navy}20`, width: "fit-content" }}>
            <Calendar size={12} color={T.navy} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>{selectedEvent.event_name}</span>
            <ChevronRight size={11} color={T.muted} />
            <span style={{ fontSize: 11, color: T.muted }}>{selectedEvent.city}, {selectedEvent.country}</span>
            <ChevronRight size={11} color={T.muted} />
            <Badge status={selectedEvent.event_status} />
          </div>
        )} */}

        {/* Screen */}
        {activeTab === "applicants" && (
          <ApplicantReview selectedEvent={selectedEvent} uc={uc} />
        )}
        {activeTab === "invitations" && (
          <InvitationManagement selectedEvent={selectedEvent} uc={uc} />
        )}
      </div>
    </div>
  );
}

export default CreatorReviewHub;